import { LitElement, html, css, unsafeHTML, map, range, t } from '@hh/global.js'

class HHEventCalendar extends LitElement {
  static properties = {
    articles: {
      state: true,
      type: Array,
    },
    filteredArticles: {
      state: true,
      type: Array,
    },
    futureOnly: {
      type: Boolean,
      attribute: 'future-only',
    },
    articlesPerPage: {
      type: Number,
      attribute: 'articles-per-page',
    },
    currentPage: {
      state: true,
      type: Number,
    },
    noArticlesFound: {
      state: true,
      type: Boolean,
    },
    dateFilter: {
      state: true,
      type: String,
    },
    typeFilter: {
      state: true,
      type: String,
    },
    allTypes: {
      state: true,
      type: Array,
    },
    allOptionValue: {
      type: String,
      attribute: 'all-option-value',
    },
  }

  constructor() {
    super()
    this.checkBrowser()

    this.articles = []
    this.articlesToRender = []
    this.currentPage = 0
    this.dateFilter = this.getParam('date')
    this.typeFilter = this.getParam('type')
  }

  async connectedCallback() {
    super.connectedCallback()
    this.articles = await this.fetchArticles()
    this.allTypes = this.getAllUniqueTypes(this.articles)
    this.addTypesToFilter(this.allTypes)

    this.initParamFilters()

    this.articlesByPage = this.splitArticlesByPage(
      this.filteredArticles,
      this.articlesPerPage
    )

    if (this.articlesByPage.length > 0) {
      this.articlesToRender = this.articlesByPage[this.currentPage]
      this.maxPages = this.articlesByPage.length
    } else {
      this.articlesToRender = []
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback()
  }

  updated(changedProperties) {
    if (changedProperties.has('filteredArticles')) {
      this.hydrateArticles()
      if (this.filteredArticles.length === 0) {
        this.noArticlesFound = true
      } else {
        this.noArticlesFound = false
      }
    }
  }

  async fetchArticles() {
    const url = `?view=events-ajax`
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error(res.statusText)

      const data = await res.json()
      const { articles } = data
      if (!articles) throw new Error('No articles found')

      return this.sortArticlesByDate(articles)
    } catch (e) {
      console.error(e)
      this.noArticlesFound = true
    }
  }

  hydrateArticles() {
    this.articlesByPage = this.splitArticlesByPage(
      this.filteredArticles,
      this.articlesPerPage
    )

    this.currentPage = 0
    if (this.articlesByPage.length > 0) {
      this.articlesToRender = this.articlesByPage[this.currentPage]
    } else {
      this.articlesToRender = []
    }
    this.maxPages = this.articlesByPage.length
    setTimeout(this.requestUpdate.bind(this), 0)
  }

  getAllUniqueTypes() {
    const types = this.articles.reduce((acc, article) => {
      if (article.types) {
        article.types.forEach((type) => {
          if (!acc.includes(type)) {
            acc.push(type)
          }
        })
      }
      return acc
    }, [])
    return [...new Set(types)]
  }

  addTypesToFilter(types) {
    types.forEach((type) => {
      let label = type
      let value = type
      this.typeFilterSlot.addOption(label, value)
    })
  }

  // Manipulate articles
  sortArticlesByDate(articles) {
    if (this.futureOnly) {
      const upcomingEvents = articles.filter((article) => {
        if (!article.date) return false
        return (
          new Date(`${article.date}T23:59:59-08:00`).getTime() >=
          new Date().getTime()
        )
      })

      return upcomingEvents.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
    } else {
      const pastEvents = articles.filter((article) => {
        if (!article.date) return false
        return (
          new Date(`${article.date}T23:59:59-08:00`).getTime() <
          new Date().getTime()
        )
      })
      return pastEvents.sort(
        (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
      )
    }
  }

  splitArticlesByPage(articles, articlesPerPage) {
    const numPages = Math.ceil(articles.length / articlesPerPage)

    return Array.from({ length: numPages }).map((_, idx) => {
      const start = idx * articlesPerPage
      const end = start + articlesPerPage
      return articles.slice(start, end)
    })
  }

  // Helpers
  hideDuplicateDates(date) {
    const dateOnPage = this.shadowRoot.querySelectorAll(`[date="${date}"]`)

    if (dateOnPage.length <= 1) return
    dateOnPage.forEach((date, idx) => {
      if (idx === 0) return
      date.classList.add('day-duplicate')
    })
  }

  initParamFilters() {
    this.safariDateFix()
    this.filterByDateAndType(this.dateFilter, this.typeFilter)
    this.syncInputsWithParams()
    this.setMinMaxDateInput()
  }

  syncInputsWithParams() {
    if (this.dateFilter) {
      const dateInput = this.dateFilterSlot.querySelector('input[type="date"]')
      if (!dateInput) return
      dateInput.value = this.dateFilter
    }

    if (this.typeFilter) {
      this.typeFilterSlot.setValueExternally(this.typeFilter)
    }
  }

  setMinMaxDateInput() {
    const dateInput = this.dateFilterSlot.querySelector('input[type="date"]')
    if (!dateInput) return

    if (this.futureOnly) {
      dateInput.min = new Date().toISOString().split('T')[0]
    } else {
      const date = new Date()
      date.setDate(date.getDate() - 1)
      dateInput.max = date.toISOString().split('T')[0]
    }
  }

  getParam(param) {
    const urlParams = new URLSearchParams(window.location.search)
    const urlParam = urlParams.get(param)
    if (!urlParam) return false
    return urlParam
  }

  updateParam(param, value) {
    const urlParams = new URLSearchParams(window.location.search)

    value === '' ? urlParams.delete(param) : urlParams.set(param, value)

    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?${urlParams}`
    )
  }

  filterByDateAndType(date, type) {
    if (date && type) {
      this.filteredArticles = this.articles.filter(
        (article) => article.date === date && article.types.includes(type)
      )
    } else if (date && !type) {
      this.filteredArticles = this.articles.filter(
        (article) => article.date === date
      )
    } else if (type && !date) {
      this.filteredArticles = this.articles.filter((article) =>
        article.types.includes(type)
      )
    } else {
      this.filteredArticles = this.articles
    }
  }

  checkBrowser() {
    if (
      (!!window.ApplePaySetupFeature || !!window.safari) &&
      this.agentHas('Safari') &&
      !this.agentHas('Chrome') &&
      !this.agentHas('CriOS')
    ) {
      document.documentElement.classList.add('is-safari')
    }

    if (
      this.agentHas('Firefox') ||
      this.agentHas('FxiOS') ||
      this.agentHas('Focus')
    ) {
      document.documentElement.classList.add('is-firefox')
    }
  }

  agentHas(keyword) {
    return navigator.userAgent.toLowerCase().search(keyword.toLowerCase()) > -1
  }

  safariDateFix(value = '') {
    const documentEle = document.documentElement

    if (value !== '' || this.getParam('date')) {
      documentEle.style.setProperty('--event-date-input-opacity', '1')
    } else {
      documentEle.style.setProperty('--event-date-input-opacity', '0')
    }
  }

  // Handlers
  onShowMore() {
    this.currentPage += 1
    this.articlesToRender = [
      ...this.articlesToRender,
      ...this.articlesByPage[this.currentPage],
    ]
  }

  onTypeChange(e) {
    const { value } = e.detail
    this.typeFilter = value
    if (value === this.allOptionValue) {
      this.typeFilter = ''
    }
    this.updateParam('type', this.typeFilter)
    this.initParamFilters()
  }

  onDateChange(e) {
    const { value } = e.target
    this.safariDateFix(value)

    this.dateFilter = value
    this.updateParam('date', this.dateFilter)
    this.initParamFilters()
  }

  onClear() {
    const dateInput = this.dateFilterSlot.querySelector('input[type="date"]')
    const clearTypeFilter = true

    if (dateInput) {
      this.safariDateFix()
      this.dateFilter = ''
      dateInput.value = this.dateFilter
      this.updateParam('date', this.dateFilter)
    }

    if (clearTypeFilter && this.typeFilter !== this.allOptionValue) {
      this.typeFilter = ''
      this.typeFilterSlot.setValueExternally(this.allOptionValue)
      this.updateParam('type', this.typeFilter)
    }

    this.initParamFilters()
  }

  // Templates
  eventTemplate(article) {
    setTimeout(() => this.hideDuplicateDates(article.date), 0)
    return html`
      <div class="day__wrapper" date="${article.date}">
        <div class="day__inner">
          <h3 class="day__date">${dayjs(article.date).format('ddd, MMM D')}</h3>

          <div class="event">
            <div class="event__content">
              <h2 class="event__title">${article.title}</h2>
              <div class="event__rte">
                ${article.content && html`${unsafeHTML(article.content)}`}
              </div>
            </div>
            <div class="event__image">
              ${article.image.src &&
              html`
                <img
                  src="${article.image.src}"
                  alt="${article.image.alt}"
                  width="${article.image.width}"
                  height="${article.image.height}"
                  loading="lazy"
                  style="aspect-ratio: ${article.image.aspect_ratio}"
                />
              `}
            </div>
          </div>
        </div>
      </div>
    `
  }

  loadingTemplate() {
    if (this.noArticlesFound) return
    return html`
      <div class="loading__wrapper">
        <div class="loading__inner">
          <div class="loading__date"></div>
          <ul class="loading__list">
            ${map(
              range(3),
              () =>
                html`
                  <li class="loading__event">
                    <div class="loading__content">
                      <div class="loading__title"></div>
                      <div class="loading__rte">
                        <div class="loading__line"></div>
                        <div class="loading__line loading__line--short"></div>
                        <div class="loading__line"></div>
                        <div class="loading__line loading__line--cta"></div>
                      </div>
                    </div>
                    <div class="loading__image"></div>
                  </li>
                `
            )}
          </ul>
        </div>
      </div>
    `
  }

  paginationTemplate() {
    if (this.noArticlesFound) return
    return html`
      <div class="pagination">
        <div>
          <span class="pagination__text">
            ${t('sections.event_calendar.showing')}
            ${this.articlesToRender.length} ${t('accessibility.of')}
            ${this.filteredArticles.length}
          </span>
          <span class="pagination__progress">
            <span
              class="pagination__progress-inner"
              style="width: ${(this.articlesToRender.length /
                this.filteredArticles.length) *
              100}%"
            ></span>
          </span>
        </div>
        ${(this.articlesToRender.length < this.filteredArticles.length &&
          html`
            <slot name="load-trigger" @click="${this.onShowMore}"></slot>
          `) ||
        ''}
      </div>
    `
  }

  noResultsTemplate() {
    return html`
      <div class="no-results__wrapper">
        <h2 class="no-results__title">
          ${t('sections.event_calendar.no_results')}
        </h2>
      </div>
    `
  }

  get typeFilterSlot() {
    const slot = this.shadowRoot.querySelector('slot[name="type-filter"]')
    return slot.assignedElements()[0] ?? null
  }

  get dateFilterSlot() {
    const slot = this.shadowRoot.querySelector('slot[name="date-filter"]')
    return slot.assignedElements()[0] ?? null
  }

  render() {
    return html`
      <div>
        <div class="filters">
          <slot name="type-filter" @change="${this.onTypeChange}"></slot>
          <slot name="date-filter" @change=${this.onDateChange}></slot>
          <slot name="clear-button" @click=${this.onClear}></slot>
        </div>
        <div>
          ${(this.noArticlesFound && this.noResultsTemplate()) || ''}
          ${this.articles.length === 0
            ? this.loadingTemplate()
            : (this.articlesToRender.length > 0 &&
                this.articlesToRender.map((article) => {
                  return this.eventTemplate(article)
                })) ||
              ''}
        </div>
        ${(this.articles.length !== 0 && this.paginationTemplate()) || ''}
      </div>
    `
  }

  static styles = css`
    .day__wrapper,
    .loading__wrapper {
      margin: 4rem 0;
    }

    .day__wrapper:first-child {
      margin-top: 0;
    }

    .day__inner,
    .loading__inner {
      border-top: 1px solid var(--hh-color-foreground);
      padding-top: 4rem;
      display: grid;
      grid-column-gap: 1rem;
    }

    .day-duplicate .day__inner {
      border-top: none;
      padding-top: 0;
    }

    @media (min-width: 750px) {
      .day__inner,
      .loading__inner {
        grid-template-columns: 1fr 2fr;
      }
    }

    .day__date,
    .event__title {
      font: var(--hh-h3-font);
      text-transform: var(--hh-h3-text-transform);
      letter-spacing: var(--hh-h3-letter-spacing);
      margin: 0;
    }

    .day__date,
    .loading__date {
      margin-bottom: 4rem;
    }

    .day-duplicate .day__date {
      display: none;
    }

    @media (min-width: 750px) {
      .day__date,
      .loading__date {
        margin: 0;
      }
    }

    .day__list,
    .loading__list {
      list-style: none;
      padding: 0;
      margin: 0;
    }

    .event,
    .loading__event {
      display: flex;
      flex-direction: column-reverse;
      gap: 1rem;
    }

    .day-duplicate .event {
      border-top: 1px solid var(--hh-color-foreground);
      padding-top: 4rem;
      grid-column-start: 2;
    }

    @media (min-width: 750px) {
      .event,
      .loading__event {
        flex-direction: row;
      }
    }

    @media (min-width: 750px) {
      .event__content,
      .loading__content {
        flex: 1 0 70%;
      }
    }

    .event__rte :is(h1, h2, h3, h4, h5, h6) {
      margin: 2rem 0 0;
    }

    .event__rte h4 {
      font: var(--hh-h4-font);
      text-transform: var(--hh-h4-text-transform);
      letter-spacing: var(--hh-h4-letter-spacing);
    }

    .event__rte h5 {
      font: var(--hh-h5-font);
      text-transform: var(--hh-h5-text-transform);
      letter-spacing: var(--hh-h5-letter-spacing);
    }

    .event__rte h6 {
      font: var(--hh-h6-font);
      text-transform: var(--hh-h6-text-transform);
      letter-spacing: var(--hh-h6-letter-spacing);
    }

    .event__rte p {
      font: var(--hh-body-font-large);
      margin: 0;
    }

    .event__rte a {
      position: relative;
      color: var(--hh-color-foreground);
      text-decoration: none;
      text-underline-offset: 0.4rem;
    }

    .event__rte a:after {
      content: '';
      position: absolute;
      left: 0;
      bottom: var(--hh-border-bottom-offset, -1px);
      background-color: currentColor;
      width: calc(100% - var(--hh-letter-spacing, 0px));
      height: var(--hh-underline-width, 2px);
    }

    .event__rte a:after {
      transform: scaleX(1);
      transform-origin: bottom left;
      transition: transform var(--hh-transition, 300ms) ease-out;
    }

    .event__rte a:hover:after {
      transform: scaleX(0);
      transform-origin: bottom right;
    }

    .event__image {
      width: 100%;
    }

    .event__image img {
      display: block;
      max-width: 100%;
      height: auto;
    }

    .loading__wrapper {
      --loading-border-radius: 5px;
      --loading-color: var(--hh-color-light);
    }

    .loading__event:not(:first-child) {
      border-top: 1px solid var(--loading-color);
      padding-top: 4rem;
    }

    .loading__event:not(:last-child) {
      padding-bottom: 4rem;
    }

    .loading__date,
    .loading__title {
      width: 162px;
      height: 62px;
      border-radius: var(--loading-border-radius);
      background-color: var(--loading-color);
    }

    .loading__image {
      width: 100%;
      height: 171px;
      border-radius: var(--loading-border-radius);
      background-color: var(--loading-color);
    }

    .loading__line {
      margin: 2rem 0 0;
      height: 20px;
      width: 50%;
      border-radius: var(--loading-border-radius);
      background-color: var(--loading-color);
    }

    .loading__line--short {
      width: 25%;
    }

    .loading__line--cta {
      height: 50px;
      width: 35%;
    }

    .no-results__wrapper {
      margin: 4rem 0;
    }

    .no-results__title {
      font: var(--hh-h1-font);
      text-transform: var(--hh-h1-text-transform);
      letter-spacing: var(--hh-h1-letter-spacing);
      text-align: center;
    }

    .pagination {
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 3rem;
    }

    .pagination__text,
    .pagination__progress,
    .pagination__progress-inner {
      display: block;
      text-align: center;
    }

    .pagination__progress {
      width: 20rem;
      height: 0.2rem;
      background-color: var(--hh-color-brand-tertiary);
      margin-top: 1rem;
      overflow: hidden;
    }

    .pagination__progress-inner {
      height: 100%;
      background-color: var(--hh-color-foreground);
    }

    .filters {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      gap: 1rem;
      margin: 4.6rem 0 10px;
    }

    ::slotted([slot='type-filter']),
    ::slotted([slot='date-filter']) {
      flex: 1 0 100%;
    }

    @media (min-width: 750px) {
      ::slotted([slot='type-filter']),
      ::slotted([slot='date-filter']) {
        flex: 1 0 calc(40% - 0.5rem);
      }
    }

    ::slotted([slot='clear-button']) {
      width: 100%;
      height: 6.6rem;
      display: flex;
      justify-content: center;
      align-items: center;
    }

    @media (min-width: 750px) {
      ::slotted([slot='clear-button']) {
        width: 12rem;
        height: unset;
      }

      .filters {
        margin: 4.6rem 0 4.6rem;
      }
    }
  `
}

customElements.define('hh-event-calendar', HHEventCalendar)
